import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { adsApi, type CampaignScheduleFilterParams } from '@/api/client';

export type DashboardLoadPhase =
  | 'idle'
  | 'campaigns'
  | 'report'
  | 'ad_groups'
  | 'keywords'
  | 'targets'
  | 'complete';

const PHASE_ORDER: DashboardLoadPhase[] = [
  'idle',
  'campaigns',
  'report',
  'ad_groups',
  'keywords',
  'targets',
  'complete',
];

export function dashboardPhaseReached(current: DashboardLoadPhase, target: DashboardLoadPhase): boolean {
  return PHASE_ORDER.indexOf(current) >= PHASE_ORDER.indexOf(target);
}

export type DashboardCatalogFilters = {
  profileId: string;
  schedule: CampaignScheduleFilterParams | null;
  dashboardStartDate: string;
  dashboardEndDate: string;
  dashboardAdProductsParam: string;
  adGroupCampaignFilter: string;
  adGroupStateFilter: 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
  normalizedAdGroupNameFilter: string;
  keywordStateFilter: 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
  keywordNextToken: string | null;
  targetCampaignFilter: string;
  targetStateFilter: 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
  targetNextToken: string | null;
};

export const DASHBOARD_SEQUENTIAL_LOAD_KEY = 'dashboard-sequential-load';

const EMPTY_KEYWORDS_DATA = { keywords: [], total_results: 0, next_token: null };

// function adGroupStates(
//   filter: DashboardCatalogFilters['adGroupStateFilter']
// ): Array<'ENABLED' | 'PAUSED' | 'ARCHIVED'> {
//   return filter === 'ALL' ? ['ENABLED', 'PAUSED', 'ARCHIVED'] : [filter];
// }

/** Runs dashboard catalog API calls in ordered chunks (no parallel burst). */
export async function runDashboardSequentialLoad(
  qc: QueryClient,
  filters: DashboardCatalogFilters,
  onPhase: (phase: DashboardLoadPhase) => void
): Promise<void> {
  const {
    profileId,
    schedule,
    dashboardStartDate,
    dashboardEndDate,
    dashboardAdProductsParam,
    // adGroupCampaignFilter,
    // adGroupStateFilter,
    // normalizedAdGroupNameFilter,
    keywordStateFilter,
    keywordNextToken,
    // targetCampaignFilter,
    // targetStateFilter,
    // targetNextToken,
  } = filters;

  onPhase('report');
  const reportRes = await adsApi.getSpReportRows(profileId, {
    startDate: dashboardStartDate,
    endDate: dashboardEndDate,
    adProducts: dashboardAdProductsParam,
  });
  if (!reportRes.success) {
    throw new Error('Failed to load campaign report metrics');
  }
  qc.setQueryData(
    ['dashboard-report-rows', profileId, dashboardStartDate, dashboardEndDate, dashboardAdProductsParam],
    reportRes.data
  );

  // Ad groups fetch disabled for now (dashboard section commented out).
  // onPhase('ad_groups');
  // const sbAgRes = await adsApi.listSbAdGroups(
  //   profileId,
  //   {
  //     include_extended_data_fields: true,
  //     campaign_ids: adGroupCampaignFilter === 'ALL' ? [] : [adGroupCampaignFilter],
  //     states: adGroupStates(adGroupStateFilter),
  //     name_match_type: 'EXACT_MATCH',
  //     names: normalizedAdGroupNameFilter ? [normalizedAdGroupNameFilter] : [],
  //     max_results: 200,
  //   },
  //   schedule
  // );
  // if (!sbAgRes.success) throw new Error('Failed to load ad groups');
  // qc.setQueryData(
  //   [
  //     'sb-ad-groups',
  //     profileId,
  //     adGroupCampaignFilter,
  //     adGroupStateFilter,
  //     normalizedAdGroupNameFilter,
  //     schedule,
  //   ],
  //   sbAgRes.data
  // );
  //
  // const spAgRes = await adsApi.listSpAdGroups(
  //   profileId,
  //   {
  //     include_extended_data_fields: true,
  //     campaign_ids: adGroupCampaignFilter === 'ALL' ? [] : [adGroupCampaignFilter],
  //     ad_group_ids: [],
  //     states: adGroupStates(adGroupStateFilter),
  //     max_results: 500,
  //   },
  //   schedule
  // );
  // if (!spAgRes.success) throw new Error('Failed to load SP ad groups');
  // qc.setQueryData(
  //   [
  //     'sp-ad-groups',
  //     profileId,
  //     adGroupCampaignFilter,
  //     adGroupStateFilter,
  //     normalizedAdGroupNameFilter,
  //     schedule,
  //   ],
  //   spAgRes.data
  // );
  //
  // const sdAgRes = await adsApi.getSdAdGroups(profileId, schedule);
  // if (!sdAgRes.success) throw new Error('Failed to load SD ad groups');
  // qc.setQueryData(['sd-ad-groups', profileId, schedule], sdAgRes.data);

  onPhase('keywords');
  let spKeywordsData: Record<string, unknown> = EMPTY_KEYWORDS_DATA;
  try {
    const spKwRes = await adsApi.listSpKeywords(
      profileId,
      {
        include_extended_data_fields: true,
        states: keywordStateFilter === 'ALL' ? [] : [keywordStateFilter],
        max_results: 200,
        next_token: keywordNextToken,
      },
      schedule
    );
    if (spKwRes.success && spKwRes.data) {
      spKeywordsData = spKwRes.data;
    }
  } catch {
    /* SP keywords optional — dashboard should still load */
  }
  qc.setQueryData(
    ['sp-keywords', profileId, keywordStateFilter, keywordNextToken, schedule],
    spKeywordsData
  );

  let sbKeywordsData: Record<string, unknown> = EMPTY_KEYWORDS_DATA;
  try {
    const sbKwRes = await adsApi.getSbKeywords(profileId, { count: 500 }, schedule);
    if (sbKwRes.success && sbKwRes.data) {
      sbKeywordsData = sbKwRes.data;
    }
  } catch {
    /* SB keywords optional */
  }
  qc.setQueryData(['sb-keywords', profileId, schedule], sbKeywordsData);

  // Targets fetch disabled for now (dashboard section commented out).
  // onPhase('targets');
  // const spTgRes = await adsApi.listSpTargets(
  //   profileId,
  //   {
  //     campaign_ids: targetCampaignFilter === 'ALL' ? [] : [targetCampaignFilter],
  //     states: targetStateFilter === 'ALL' ? [] : [targetStateFilter],
  //     max_results: 200,
  //     next_token: targetNextToken,
  //   },
  //   schedule
  // );
  // if (!spTgRes.success) throw new Error('Failed to load targets');
  // qc.setQueryData(
  //   ['sp-targets', profileId, targetCampaignFilter, targetStateFilter, targetNextToken, schedule],
  //   spTgRes.data
  // );
  //
  // const sbTgRes = await adsApi.listSbTargets(profileId, { max_results: 500 }, schedule);
  // if (!sbTgRes.success) throw new Error('Failed to load SB targets');
  // qc.setQueryData(['sb-targets', profileId, schedule], sbTgRes.data);
  //
  // const sdTgRes = await adsApi.getSdTargets(profileId, schedule);
  // if (!sdTgRes.success) throw new Error('Failed to load SD targets');
  // qc.setQueryData(['sd-targets', profileId, schedule], sdTgRes.data);
  //
  // const sdPaRes = await adsApi.getSdProductAds(profileId, schedule);
  // if (!sdPaRes.success) throw new Error('Failed to load SD product ads');
  // qc.setQueryData(['sd-product-ads', profileId, schedule], sdPaRes.data);

  onPhase('complete');
}

export function useDashboardSequentialLoad(
  filters: DashboardCatalogFilters,
  enabled: boolean
) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<DashboardLoadPhase>('idle');

  const query = useQuery({
    queryKey: [
      DASHBOARD_SEQUENTIAL_LOAD_KEY,
      filters.profileId,
      filters.schedule,
      filters.dashboardStartDate,
      filters.dashboardEndDate,
      filters.dashboardAdProductsParam,
      filters.adGroupCampaignFilter,
      filters.adGroupStateFilter,
      filters.normalizedAdGroupNameFilter,
      filters.keywordStateFilter,
      filters.keywordNextToken,
      filters.targetCampaignFilter,
      filters.targetStateFilter,
      filters.targetNextToken,
    ],
    queryFn: async () => {
      setPhase('report');
      await runDashboardSequentialLoad(qc, filters, setPhase);
      return true;
    },
    enabled,
    staleTime: 0,
    retry: 1,
  });

  const isLoading = query.isFetching;
  const effectivePhase: DashboardLoadPhase = isLoading
    ? phase
    : query.isSuccess
      ? 'complete'
      : phase;

  return {
    phase: effectivePhase,
    isLoading,
    error: query.error,
    refetch: query.refetch,
    isSuccess: query.isSuccess,
  };
}
